'use client';

import { useEffect, useState, type ComponentType } from 'react';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';

export type LightboxComponentProps = {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  images: any[];
  zoom?: { maxZoomPixelRatio: number; zoomInMultiplier?: number; doubleTapDelay?: number };
};

let runtimePromise: Promise<typeof import('./lightbox-runtime')> | undefined;

function loadRuntime() {
  runtimePromise ??= import('./lightbox-runtime').catch(error => {
    runtimePromise = undefined;
    throw error;
  });
  return runtimePromise;
}

export function LightboxComponent(props: LightboxComponentProps) {
  const [Runtime, setRuntime] = useState<ComponentType<LightboxComponentProps> | null>(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!props.isOpen || Runtime) return;
    let cancelled = false;
    setFailed(false);
    loadRuntime()
      .then(loaded => {
        if (!cancelled) setRuntime(() => loaded.LightboxRuntime);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [props.isOpen, Runtime, attempt]);

  if (!props.isOpen) return null;
  if (Runtime) return <Runtime {...props} />;
  return (
    <GalleryPending
      failed={failed}
      retry={() => setAttempt(value => value + 1)}
      close={() => props.setIsOpen(false)}
    />
  );
}

function GalleryPending({
  failed,
  retry,
  close,
}: {
  failed: boolean;
  retry: () => void;
  close: () => void;
}) {
  const locale = useLocaleStore(state => state.currentLocale);
  const copy = locale?.startsWith('cs')
    ? {
        loading: 'Načítání galerie…',
        error: 'Galerii se nepodařilo načíst.',
        retry: 'Zkusit znovu',
        close: 'Zavřít',
      }
    : locale?.startsWith('sk')
      ? {
          loading: 'Načítavanie galérie…',
          error: 'Galériu sa nepodarilo načítať.',
          retry: 'Skúsiť znova',
          close: 'Zavrieť',
        }
      : {
          loading: 'Loading gallery…',
          error: 'Could not load the gallery.',
          retry: 'Try again',
          close: 'Close',
        };
  return (
    <div className="fixed bottom-4 left-4 z-[100001] flex max-w-[calc(100vw-2rem)] items-center gap-3 rounded-lg border border-border bg-white p-4 text-foreground shadow-lg">
      <span role={failed ? 'alert' : 'status'}>{failed ? copy.error : copy.loading}</span>
      {failed && (
        <button type="button" onClick={retry} className="underline">
          {copy.retry}
        </button>
      )}
      <button type="button" onClick={close} className="underline">
        {copy.close}
      </button>
    </div>
  );
}
